/**
 * BillEditModal Component
 * 
 * A modal component for editing bill details within a load form settlement.
 * Allows users to modify SKU quantities, add new SKUs, and remove existing ones.
 * 
 * Features:
 * - Real-time quantity calculations (PCS = CTN * packing)
 * - SKU selection from complete catalog (up to 50 SKUs)
 * - View-only fields for discount, advance tax, and existing SKU details
 * - Editable quantity (CTN) for existing SKUs
 * - Full editing capabilities for new SKUs
 * - API integration for bill updates
 * 
 * Data Structure:
 * - Uses actual packing values from SKU data (no defaults)
 * - Calculates amounts as CTN * rate (simplified)
 * - Maintains data integrity with proper null checks
 * 
 * @param open - Controls modal visibility
 * @param onClose - Callback when modal is closed
 * @param billData - Bill data including SKU items and metadata
 * @param onSave - Callback when bill is saved with updated data
 */

import React, { useState, useEffect } from 'react';
import { formatCurrency, formatQuantity } from '../../utils/formatters';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { getCompanyUom } from '../../utils/companyUom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useSKUs } from '../../hooks/api/useSKUs';
import { useToast } from '../../contexts/ToastContext';
import { loadFormService } from '../../services/loadforms/loadFormService';

interface SKUItem {
  _id: string;
  sku_id: {
    _id: string;
    name: string;
    packing?: number;
  };
  quantity_ctn: number;
  loose_pcs: number;
  quantity_pcs: number;
  price: number;
  amount: number;
  discount: number;
}

interface BillEditModalProps {
  open: boolean;
  onClose: () => void;
  billData: any;
  onSave: (updatedBill: any) => void;
}

const BillEditModal: React.FC<BillEditModalProps> = ({
  open,
  onClose,
  billData,
  onSave,
}) => {
    const { user } = useSelector((state: RootState) => state.auth);
    const uom = getCompanyUom(user?.company_uom);
  const { showToast } = useToast();
  const { data: skusData, isLoading: isSKUsLoading } = useSKUs({
    limit: 50,
    active_only: true,
  });
  const [skuItems, setSkuItems] = useState<SKUItem[]>([]);
  const [discount, setDiscount] = useState(0);
  const [advTax, setAdvTax] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (billData) {
      // Update existing SKUs to include packing information
      const updatedSkuItems = (billData.sku_items || []).map((item: any) => ({
        ...item,
        quantity_ctn: item.full_quantity_ctn ?? item.quantity_ctn,
        loose_pcs: item.loose_pcs ?? 0,
        sku_id: {
          ...item.sku_id,
          packing: item.sku_id?.packing,
        },
      }));
      setSkuItems(updatedSkuItems);
      setDiscount(billData.discount || 0);
      setAdvTax(billData.adv_tax || 0);
    }
  }, [billData]);

  /**
   * Adds a new SKU row to the bill for editing
   * Pre-fills with the first available SKU from the catalog
   */
  const handleAddSKU = () => {
    if (skusData?.results && skusData.results.length > 0) {
      const firstSKU = skusData.results[0];
      const newSKU: SKUItem = {
        _id: `temp_${Date.now()}`,
        sku_id: {
          _id: firstSKU._id,
          name: firstSKU.name,
          packing: firstSKU.packing,
        },
        quantity_ctn: 0,
        loose_pcs: 0,
        quantity_pcs: 0,
        price: firstSKU.standard_rate,
        amount: 0,
        discount: 0,
      };
      setSkuItems((prev) => [...prev, newSKU]);
    }
  };

  const handleRemoveSKU = (index: number) => {
    setSkuItems((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * Handles changes to SKU fields with real-time calculations
   * Recalculate total pieces and the estimated amount from primary units and loose pieces.
   */
  const handleSKUChange = (index: number, field: keyof SKUItem, value: any) => {
    const isNewSKU = skuItems[index]._id.startsWith('temp_');
    if (
      field === 'quantity_ctn' || field === 'loose_pcs' ||
      (isNewSKU && field === 'price')
    ) {
      setSkuItems((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [field]: value };

        const item = updated[index];
        const packing = item.sku_id?.packing || 1;
        if (item.quantity_ctn < 0 || !Number.isInteger(item.loose_pcs) || item.loose_pcs < 0 || item.loose_pcs >= packing) return prev;
        item.quantity_pcs = item.quantity_ctn * packing + item.loose_pcs;
        item.amount = item.price * (item.quantity_ctn + item.loose_pcs / packing);
        return updated;
      });
    }
  };

  const handleSKUIdChange = (index: number, skuId: string) => {
    // Only allow SKU selection for new SKUs (temp IDs)
    if (skuItems[index]._id.startsWith('temp_')) {
      const selectedSKU = skusData?.results?.find((sku) => sku._id === skuId);
      if (selectedSKU) {
        setSkuItems((prev) => {
          const updated = [...prev];
          const packing = selectedSKU.packing;
          updated[index] = {
            ...updated[index],
            sku_id: {
              _id: selectedSKU._id,
              name: selectedSKU.name,
              packing: packing,
            },
            price: selectedSKU.standard_rate,
            loose_pcs: 0,
            quantity_pcs: updated[index].quantity_ctn * packing,
            amount: updated[index].quantity_ctn * selectedSKU.standard_rate,
          };
          return updated;
        });
      }
    }
  };

  const calculateTotals = () => {
    const totalQuantityCtn = skuItems.reduce(
      (sum, item) => sum + (item.quantity_ctn || 0) + item.loose_pcs / (item.sku_id.packing || 1),
      0
    );
    const totalQuantityPcs = skuItems.reduce(
      (sum, item) => sum + (item.quantity_pcs || 0),
      0
    );
    const netAmount = skuItems.reduce(
      (sum, item) => sum + (item.amount || 0),
      0
    );
    const discountAmount = (netAmount * discount) / 100;
    const total = netAmount - discountAmount;
    const advTaxAmount = (total * advTax) / 100;
    const netPayable = total + advTaxAmount;

    return {
      quantity_ctn: totalQuantityCtn,
      quantity_pcs: totalQuantityPcs,
      net_amount: netAmount,
      discount: discountAmount,
      total: total,
      adv_tax: advTaxAmount,
      net_payable: netPayable,
    };
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const summary = calculateTotals();

      const apiSkuItems = skuItems.map((item) => ({
        sku_id: item.sku_id._id,
        quantity_ctn: item.quantity_ctn,
        loose_pcs: item.loose_pcs,
      }));

      // Get customer ID from bill data
      const customerId = typeof billData.customer_id === 'string' 
        ? billData.customer_id 
        : billData.customer_id?._id;

      if (!customerId) {
        throw new Error('Customer ID not found in bill data');
      }

      // Call update bill API with correct payload structure (removed area field)
      await loadFormService.updateBill(billData._id, {
        customer_id: customerId,
        sku_items: apiSkuItems,
        remarks: `Updated order with modified quantities`,
      });

      const updatedBill = {
        ...billData,
        sku_items: skuItems,
        summary,
        discount,
        adv_tax: advTax,
      };

      onSave(updatedBill);
      showToast('Bill updated successfully', 'success');
      onClose();
    } catch (error) {
      showToast('Failed to update bill', 'error');
      console.error('Error updating bill:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const totals = calculateTotals();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Edit Bill - {billData?.customer_name}
          </Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddSKU}
            variant="outlined"
            size="small"
          >
            Add SKU
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        {isSKUsLoading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Tax and Discount Display (View-only) */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <Box
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'grey.300',
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Discount %
                  </Typography>
                  <Typography variant="h6">{discount}%</Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'grey.300',
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Advance Tax %
                  </Typography>
                  <Typography variant="h6">{advTax}%</Typography>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ mb: 2 }} />

            {/* SKU Items Table */}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>SKU Name</TableCell>
                    <TableCell>Qty ({uom.code})</TableCell>
                    <TableCell>Loose PCS</TableCell>
                    <TableCell>Qty (PCS)</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {skuItems.map((item, index) => {
                    const isNewSKU = item._id.startsWith('temp_');
                    return (
                      <TableRow key={item._id}>
                        <TableCell>
                          {isNewSKU ? (
                            <FormControl fullWidth size="small">
                              <Select
                                value={item.sku_id._id}
                                onChange={(e) =>
                                  handleSKUIdChange(index, e.target.value)
                                }
                              >
                                {skusData?.results?.map((sku) => (
                                  <MenuItem key={sku._id} value={sku._id}>
                                    {sku.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : (
                            <Typography variant="body2" sx={{ py: 1 }}>
                              {item.sku_id.name}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={item.quantity_ctn}
                            onChange={(e) =>
                              handleSKUChange(
                                index,
                                'quantity_ctn',
                                Number(e.target.value)
                              )
                            }
                            inputProps={{
                              style: { MozAppearance: 'textfield' },
                            }}
                            sx={{
                              width: 100,
                              '& input[type=number]': {
                                MozAppearance: 'textfield',
                              },
                              '& input[type=number]::-webkit-outer-spin-button':
                                {
                                  WebkitAppearance: 'none',
                                  margin: 0,
                                },
                              '& input[type=number]::-webkit-inner-spin-button':
                                {
                                  WebkitAppearance: 'none',
                                  margin: 0,
                                },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={item.loose_pcs}
                            onChange={(e) => handleSKUChange(index, 'loose_pcs', Number(e.target.value))}
                            inputProps={{ min: 0, max: (item.sku_id.packing || 1) - 1, step: 1 }}
                            sx={{ width: 90 }}
                          />
                        </TableCell>
                        <TableCell>{formatQuantity(item.quantity_pcs)}</TableCell>
                        <TableCell>
                          {isNewSKU ? (
                            <TextField
                              size="small"
                              type="number"
                              value={item.price}
                              onChange={(e) =>
                                handleSKUChange(
                                  index,
                                  'price',
                                  Number(e.target.value)
                                )
                              }
                              inputProps={{
                                style: { MozAppearance: 'textfield' },
                              }}
                              sx={{
                                width: 100,
                                '& input[type=number]': {
                                  MozAppearance: 'textfield',
                                },
                                '& input[type=number]::-webkit-outer-spin-button':
                                  {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                  },
                                '& input[type=number]::-webkit-inner-spin-button':
                                  {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                  },
                              }}
                            />
                          ) : (
                            <Typography variant="body2" sx={{ py: 1 }}>
                              Rs {formatCurrency(item.price)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>Rs {formatCurrency(item.amount)}</TableCell>
                        <TableCell>
                          <IconButton
                            color="error"
                            onClick={() => handleRemoveSKU(index)}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Summary */}
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                Bill Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Total Quantity ({uom.code}):</strong> {formatQuantity(totals.quantity_ctn)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Total Quantity (PCS):</strong> {formatQuantity(totals.quantity_pcs)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Total:</strong> Rs{' '}
                    {formatCurrency(totals.total)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Advance Tax ({advTax}%):</strong> Rs{' '}
                    {formatCurrency(totals.adv_tax)}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="primary.main"
                    sx={{ fontWeight: 'bold' }}
                  >
                    <strong>Net Payable:</strong> Rs{' '}
                    {formatCurrency(totals.net_payable)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} startIcon={<CancelIcon />}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BillEditModal;
